import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import { getRoleHomePath } from '../../utils/roleHomePath';
import OrganizationContactPanel from '../../components/Common/OrganizationContactPanel';
import PublicLegalPageLayout, {
  ContactInfo,
  FooterText,
  LabeledListItem,
  List,
  Section,
  SectionTitle,
  Text,
  TextWithTopMargin
} from '../../components/Common/PublicLegalPageLayout';
import SupportAlertBanner from '../../components/Common/SupportAlertBanner';
import PublicResourceLinksList from '../../components/Common/PublicResourceLinks';
import SupportFaqSection from '../../components/Support/SupportFaqSection';
import usePublicPagesSettings from '../../hooks/usePublicPagesSettings';
import { pickLocaleText } from '../../utils/publicPagesDefaults';

const SupportTechnique = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const year = new Date().getFullYear();
  const { settings } = usePublicPagesSettings({ publicAccess: true });
  const locale = i18n.language;

  const slaLevels = settings?.support?.sla || [];

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <PublicLegalPageLayout
      title={t('supportPage.title')}
      backLabel={t('supportPage.back')}
      onBack={() => navigate(user ? getRoleHomePath(user.role) : '/')}
    >
      <SupportAlertBanner banners={settings?.support?.banners} />

      <Section>
        <SectionTitle>{t('supportPage.contact.title')}</SectionTitle>
        <Text>{t('supportPage.contact.intro')}</Text>
        <ContactInfo>
          <OrganizationContactPanel publicAccess onDark />
        </ContactInfo>
      </Section>

      <Section>
        <SectionTitle>{t('supportPage.supportTypes.title')}</SectionTitle>
        <Text>{t('supportPage.supportTypes.intro')}</Text>
        <List>
          <LabeledListItem label={t('supportPage.supportTypes.general.label')} text={t('supportPage.supportTypes.general.text')} />
          <LabeledListItem label={t('supportPage.supportTypes.connection.label')} text={t('supportPage.supportTypes.connection.text')} />
          <LabeledListItem label={t('supportPage.supportTypes.interventions.label')} text={t('supportPage.supportTypes.interventions.text')} />
          <LabeledListItem label={t('supportPage.supportTypes.equipment.label')} text={t('supportPage.supportTypes.equipment.text')} />
          <LabeledListItem label={t('supportPage.supportTypes.reports.label')} text={t('supportPage.supportTypes.reports.text')} />
          <LabeledListItem label={t('supportPage.supportTypes.training.label')} text={t('supportPage.supportTypes.training.text')} />
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('supportPage.responseTime.title')}</SectionTitle>
        {slaLevels.length > 0 ? (
          <List>
            {slaLevels.map((level) => (
              <LabeledListItem
                key={level.id}
                label={pickLocaleText(level.labels, locale)}
                text={pickLocaleText(level.delays, locale)}
              />
            ))}
          </List>
        ) : (
          <List>
            <LabeledListItem label={t('supportPage.responseTime.urgent.label')} text={t('supportPage.responseTime.urgent.text')} />
            <LabeledListItem label={t('supportPage.responseTime.high.label')} text={t('supportPage.responseTime.high.text')} />
            <LabeledListItem label={t('supportPage.responseTime.normal.label')} text={t('supportPage.responseTime.normal.text')} />
            <LabeledListItem label={t('supportPage.responseTime.general.label')} text={t('supportPage.responseTime.general.text')} />
          </List>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('supportPage.resources.title')}</SectionTitle>
        <Text>{t('supportPage.resources.intro')}</Text>
        <PublicResourceLinksList resources={settings?.support?.resources} />
      </Section>

      <Section id="faq">
        <SupportFaqSection />
      </Section>

      <Section>
        <SectionTitle>{t('supportPage.commonIssues.title')}</SectionTitle>
        <Text><strong>{t('supportPage.commonIssues.loginTitle')}</strong></Text>
        <List>
          <li>{t('supportPage.commonIssues.loginStep1')}</li>
          <li>{t('supportPage.commonIssues.loginStep2')}</li>
          <li>{t('supportPage.commonIssues.loginStep3')}</li>
          <li>{t('supportPage.commonIssues.loginStep4')}</li>
        </List>
        <TextWithTopMargin><strong>{t('supportPage.commonIssues.slowTitle')}</strong></TextWithTopMargin>
        <List>
          <li>{t('supportPage.commonIssues.slowStep1')}</li>
          <li>{t('supportPage.commonIssues.slowStep2')}</li>
          <li>{t('supportPage.commonIssues.slowStep3')}</li>
          <li>{t('supportPage.commonIssues.slowStep4')}</li>
        </List>
        <TextWithTopMargin><strong>{t('supportPage.commonIssues.notifTitle')}</strong></TextWithTopMargin>
        <List>
          <li>{t('supportPage.commonIssues.notifStep1')}</li>
          <li>{t('supportPage.commonIssues.notifStep2')}</li>
          <li>{t('supportPage.commonIssues.notifStep3')}</li>
          <li>{t('supportPage.commonIssues.notifStep4')}</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('supportPage.infoToProvide.title')}</SectionTitle>
        <Text>{t('supportPage.infoToProvide.intro')}</Text>
        <List>
          <li>{t('supportPage.infoToProvide.item1')}</li>
          <li>{t('supportPage.infoToProvide.item2')}</li>
          <li>{t('supportPage.infoToProvide.item3')}</li>
          <li>{t('supportPage.infoToProvide.item4')}</li>
          <li>{t('supportPage.infoToProvide.item5')}</li>
          <li>{t('supportPage.infoToProvide.item6')}</li>
        </List>
      </Section>

      <Section>
        <FooterText>
          <strong>{t('supportPage.footer.appName')}</strong>
          <br />
          {t('supportPage.footer.rights', { year })}
        </FooterText>
      </Section>
    </PublicLegalPageLayout>
  );
};

export default SupportTechnique;
